//
// config.ts — where the data comes from, and how it gets here
//
// Two kinds of endpoint live in this file, and the difference matters more
// than it looks.
//
// **Direct** endpoints answer with `Access-Control-Allow-Origin: *`, so the
// browser calls them itself. CelesTrak, Open-Meteo (both services) and NASA
// all do.
//
// **Proxied** endpoints do not, and cannot be fixed from the page:
//   - adsb.lol sends no CORS header at all, so the browser discards the
//     response no matter what the page asks for.
//   - Planespotters requires a descriptive `User-Agent`, and a browser will
//     not let a page set that header.
// Both are solved the same way — a same-origin path that a server rewrites.
//
// CelesTrak is a third case: reachable, but rate-limited by a rule an
// in-browser cache cannot satisfy. It goes through the proxy too. See its
// constant below.
// In development that server is Vite (`vite.config.ts`); in production it is
// nginx on axispace.org (`scripts/nginx-axispace.org.conf`). The two define
// the *same* prefixes, so the app never knows or cares which it is talking
// to, and there is no `import.meta.env.DEV` branch anywhere.
//

/** Live ADS-B aircraft. Proxied — see the header. */
export const ADSB_BASE = "/feeds/adsb";

/** Aircraft photographs. Proxied — see the header. */
export const PHOTOS_BASE = "/feeds/photos";

/**
 * CelesTrak GP element sets, through AZISUR's proxy.
 *
 * CelesTrak is CORS-open, so unlike the two above this one *could* be
 * called directly — and it was, until 2026-08-21, when the live site showed
 * why not. CelesTrak enforces fair use by refusing you: re-request a group
 * inside its two-hour publication window and you get a 403 reading "GP data
 * has not updated since your last successful download." Every page reload
 * asked again, so from the second reload onward Starlink silently
 * disappeared from the map.
 *
 * An in-browser cache cannot fix that — a reload empties it. The server
 * cache can, and one download per group per window is exactly what
 * CelesTrak asks for.
 */
export const CELESTRAK_GP = "/feeds/celestrak/gp.php";

/** Open-Meteo forecast. Direct — CORS-open, no key. */
export const OPEN_METEO_FORECAST = "https://api.open-meteo.com/v1/forecast";

/** Open-Meteo geocoding — a separate service from the forecast. Direct. */
export const OPEN_METEO_GEOCODING = "https://geocoding-api.open-meteo.com/v1/search";

/** NASA Near-Earth Object Web Service. Direct. */
export const NASA_NEO_FEED = "https://api.nasa.gov/neo/rest/v1/feed";

/**
 * NASA's shared demo key. Heavily rate-limited and shared by the entire
 * internet, so NEO lookups will intermittently fail — `neo.ts` falls back
 * to a named catalogue when they do.
 *
 * A browser cannot keep a secret: anything shipped here is readable by
 * anyone who opens devtools. A private key therefore belongs behind the
 * nginx proxy, not in this file. The iOS app, which *can* hold a key, uses
 * the real one from its Secrets.plist.
 */
export const NASA_API_KEY = "DEMO_KEY";

/** Fetch with a timeout, since several of these feeds can hang rather than fail. */
export async function fetchJSON<T>(url: string, timeoutMs = 15000): Promise<T | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
